import React from 'react';
import { CheckIcon, WarningIcon } from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFButton,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';

import { toAceiteTermos, type AceiteTermosData } from './helpers';

interface ViewProps {
  data: AceiteTermosData;
  isEditMode?: boolean;
}

/**
 * Caixa de aceite + botão. O `<a>` só existe depois do aceite: antes dele o
 * botão é um `<button>` que mostra o aviso, então nem teclado, nem clique do
 * meio, nem "copiar link" chegam ao destino sem a caixa marcada.
 *
 * Sem `aria-disabled` no botão bloqueado: o `_buttons.scss` do DS põe
 * `pointer-events: none` nele, e o clique que dispara o aviso nunca chegaria.
 *
 * O aviso é inline (`role="alert"`), não `window.alert`: é lido pelo leitor de
 * tela, segue o alto contraste e não bloqueia a página. A área dele fica
 * sempre no DOM para abrir e fechar com transição em vez de surgir de golpe.
 */
export const MpfAceiteTermosBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
}) => {
  const checkboxId = React.useId();
  const avisoId = React.useId();
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  const [aceito, setAceito] = React.useState(false);
  const [tentou, setTentou] = React.useState(false);
  const [tentativas, setTentativas] = React.useState(0);

  const aceite = toAceiteTermos(data);
  const mostrarAviso = !isEditMode && tentou && !aceito;

  if (!aceite.href && !isEditMode) return null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAceito(event.target.checked);
    if (event.target.checked) setTentou(false);
  };

  const handleBloqueado = () => {
    setTentou(true);
    setTentativas((n) => n + 1);
    checkboxRef.current?.focus();
  };

  let botao: React.ReactNode;
  if (isEditMode || !aceite.href) {
    botao = (
      <MPFButton as="span" variant="primary" size="m">
        {aceite.botaoRotulo}
      </MPFButton>
    );
  } else if (aceito) {
    botao = (
      <MPFButton
        as={MPFLink}
        href={aceite.href}
        openLinkInNewTab={aceite.novaAba || undefined}
        variant="primary"
        size="m"
      >
        {aceite.botaoRotulo}
      </MPFButton>
    );
  } else {
    botao = (
      <MPFButton
        type="button"
        variant="primary"
        size="m"
        aria-describedby={mostrarAviso ? avisoId : undefined}
        onClick={handleBloqueado}
      >
        {aceite.botaoRotulo}
      </MPFButton>
    );
  }

  return (
    <div className="mpf-aceite-termos not-prose">
      <div className="mpf-aceite-termos__campo">
        <span className="mpf-aceite-termos__caixa">
          <input
            ref={checkboxRef}
            id={checkboxId}
            type="checkbox"
            className="mpf-aceite-termos__checkbox"
            checked={aceito}
            onChange={handleChange}
            aria-invalid={mostrarAviso || undefined}
            aria-describedby={mostrarAviso ? avisoId : undefined}
          />
          <CheckIcon
            aria-hidden="true"
            weight="bold"
            className="mpf-aceite-termos__marca"
          />
        </span>
        <label htmlFor={checkboxId} className="mpf-aceite-termos__label">
          {aceite.texto}
        </label>
      </div>

      <div className="mpf-aceite-termos__acoes">
        {botao}

        {!isEditMode && (
          <div
            className={
              mostrarAviso
                ? 'mpf-aceite-termos__aviso-area mpf-aceite-termos__aviso-area--visivel'
                : 'mpf-aceite-termos__aviso-area'
            }
            aria-hidden={!mostrarAviso}
          >
            <div className="mpf-aceite-termos__aviso-recorte">
              {/* `key` remonta o alerta a cada tentativa, para o leitor de tela
                anunciar de novo; o texto fica na saída, para sumir com o fade. */}
              <p
                key={tentativas}
                id={avisoId}
                role="alert"
                className="mpf-aceite-termos__aviso"
              >
                {tentativas > 0 && (
                  <>
                    <WarningIcon
                      aria-hidden="true"
                      weight="fill"
                      className="mpf-aceite-termos__aviso-icone"
                    />
                    <span>{aceite.aviso}</span>
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {isEditMode && !aceite.href && (
        <p className="mpf-aceite-termos__oculto">
          Não aparece no site: falta o destino do botão.
        </p>
      )}
    </div>
  );
};

export default withBlockExtensions(MpfAceiteTermosBlockView);
